import {
  Box,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import MenuIcon from "@mui/icons-material/Menu"; // Added for mobile menu icon
import {
  getAllQuestion,
  getListOfStep,
  getQuestionById,
  updateOrgaProfile,
} from "../../redux/actions/organizationProfileCreators";
import EditQuestions from "./EditQuestions";
import StepList from "./StepList";
import DataLoading from "../DataLoading";

export default function EditOrganizationProfile({
  step_id,
  question_id,
  stepComleted,
}) {
  const history = useHistory();
  const { isAuth } = useSelector((state) => state.user);
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width:768px)");
  const orga_id = localStorage.getItem("orgaId");
  const listOfSteps = useSelector(
    (state) => state.organizationProfile.listOfSteps
  );
  const dispatch = useDispatch();
  const [stepId, setStepId] = useState(step_id || listOfSteps[0]?.identifier);
  const [isUpdating, setUpdating] = useState(false);
  const [visibleQuestions, setVisibleQuestions] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [listOfQuestionsByStepId, setListOfQuestionsByStepId] = useState([]);
  const [lastAddedIdentifierByNext, setLastAddedIdentifierByNext] =
    useState(null);
  const [loadingText, setLoadingText] = useState("Loading");
  const [questionToUpdate, setQuestionToUpdate] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const questionRefs = useRef([]);
  const [filteredSteps, setFilteredSteps] = useState(listOfSteps);

  // functions
  async function saveUpdates() {
    setUpdating(true);
    let res = null;
    const updateValue = questionToUpdate;
    setQuestionToUpdate([]);
    if (updateValue.length > 0) {
      res = await updateOrgaProfile({ updates: updateValue }, orga_id);
    }
    setUpdating(false);
    return res;
  }

  function addToUpdateQuestions(question, value) {
    const data = {
      key: question.key,
      value: value,
      associated_step_id: question.associated_step_id,
    };

    // Check if the question already exists in the state
    setQuestionToUpdate((prevQuestions) => {
      const questionExists = prevQuestions.some(
        (q) =>
          q.key === data.key && q.associated_step_id === data.associated_step_id
      );

      // If the question doesn't exist, add it to the state
      if (!questionExists) {
        return [...prevQuestions, data];
      }

      // If the question exists, return the previous state without changes
      return prevQuestions;
    });
  }

  function deleteFromQuestionToUpdate(question) {
    setQuestionToUpdate((prevQuestions) => {
      const index = prevQuestions.findIndex(
        (q) =>
          q.key === question.key &&
          q.associated_step_id === question.associated_step_id
      );
      if (index !== -1) {
        const updatedQuestions = prevQuestions.slice(0, index);
        return updatedQuestions;
      }
      return prevQuestions;
    });
  }

  async function handleGoBack() {
    await setVisibleQuestions([]);
    setIsLoaded(true);
    history.push("/profile");
  }

  function addQuestion(question) {
    setVisibleQuestions((prev) => {
      const isQuestionAlreadyAdded = prev.some(
        (q) => q.identifier === question.identifier
      );
      if (!isQuestionAlreadyAdded) {
        const updatedQuestions = [...prev, question];
        setTimeout(() => {
          const lastQuestionElement =
            questionRefs.current[updatedQuestions.length - 1];
          if (lastQuestionElement) {
            lastQuestionElement.scrollIntoView({
              behavior: "smooth",
              block: "end",
            });
          }
        }, 0);
        return updatedQuestions;
      }
      return prev;
    });
  }

  function deleteQuestionsAfterIndex(question) {
    setVisibleQuestions((prev) => {
      const index = prev.findIndex((q) => q.identifier === question.identifier);
      if (index !== -1) {
        const updatedVisibleQuestions = prev.slice(0, index + 1);
        const lastRemainingQuestion = updatedVisibleQuestions
          .slice()
          .reverse()
          .find((q) =>
            listOfQuestionsByStepId.some(
              (listQ) => listQ.identifier === q.identifier
            )
          );
        if (lastRemainingQuestion) {
          setLastAddedIdentifierByNext(lastRemainingQuestion.identifier);
        }
        return updatedVisibleQuestions;
      }
      return prev;
    });
  }

  function isVisible(question) {
    return (
      visibleQuestions[visibleQuestions.length - 1].identifier ===
      question.identifier
    );
  }

  // async function goToNextQuestion(question = null) {
  //   if (question) {
  //     addQuestion(question);
  //     return;
  //   }

  //   const lastVisibleQuestion = visibleQuestions[visibleQuestions.length - 1];
  //   const lastVisibleQuestionIndex = listOfQuestionsByStepId.findIndex(
  //       (q) =>
  //           q.identifier === (lastAddedIdentifierByNext || lastVisibleQuestion?.identifier)
  //   );

  //   if (lastVisibleQuestionIndex + 1 < listOfQuestionsByStepId.length) {
  //     const nextQuestion = listOfQuestionsByStepId[lastVisibleQuestionIndex + 1];
  //     addQuestion(nextQuestion);
  //     setLastAddedIdentifierByNext(nextQuestion.identifier);
  //   } else {
  //     const currentStepIndex = listOfSteps.findIndex((step) => step.identifier === stepId);
  //     const nextStep = listOfSteps[currentStepIndex + 1];

  //     if (nextStep) {
  //       if(stepComleted && question_id && questionToUpdate.length===0){
  //         handleGoBack();
  //       }
  //          handleChangeValue(null, nextStep.identifier);
  //     } else {
  //       handleGoBack();
  //     }
  //   }
  // }

  async function goToNextQuestion(question = null, isSkip) {
    if (question) {
      addQuestion(question);
      return;
    }

    const lastVisibleQuestion = visibleQuestions[visibleQuestions.length - 1];
    const lastVisibleQuestionIndex = listOfQuestionsByStepId.findIndex(
      (q) =>
        q.identifier ===
        (lastAddedIdentifierByNext || lastVisibleQuestion?.identifier)
    );

    if (lastVisibleQuestionIndex + 1 < listOfQuestionsByStepId.length) {
      const nextQuestion =
        listOfQuestionsByStepId[lastVisibleQuestionIndex + 1];
      addQuestion(nextQuestion);
      setLastAddedIdentifierByNext(nextQuestion.identifier);
    } else {
      const currentStepIndex = listOfSteps.findIndex(
        (step) => step.identifier === stepId
      );

      // Find the next step where is_completed !== true
      let nextStepIndex = currentStepIndex + 1;

      while (
        nextStepIndex < listOfSteps.length &&
        listOfSteps[nextStepIndex].is_completed === true
      ) {
        nextStepIndex++; // Skip steps that are already completed
      }

      const nextStep = listOfSteps[nextStepIndex];

      if (nextStep && !stepComleted) {
        console.log("condition 1");
        handleChangeValue(null, nextStep.identifier);
      } else if (isSkip && stepComleted) {
        console.log("condition 2");
        setIsLoaded(false);
        await handleGoBack();
      }
      // } else if (stepComleted && !nextStep && !isSkip) {
      //     setTimeout(() => {
      //         if (questionToUpdate.length === 0) {
      //             handleGoBack();
      //         }
      //     }, 700)
      //     console.log('condition 3')
      //     setIsLoaded(true);
      //     setLoadingText("Saving")
      //     setVisibleQuestions([]);
      // }
      else {
        console.log("condition 4");
        setIsLoaded(true);
        setLoadingText("Saving");
        await setVisibleQuestions([]);
        setTimeout(async () => {
          setLoadingText("Loading");
          await handleGoBack();
        }, 1000);
      }
    }
  }

  function handleChangeValue(event, newStepId) {
    setVisibleQuestions([]);
    setLastAddedIdentifierByNext(null);
    setStepId(newStepId);
  }

  async function fetchSteps() {
    await dispatch(getListOfStep(orga_id));
  }

  async function fetchQuestionsByStep() {
    setIsLoaded(true);
    let questions = [];
    try {
      if (stepComleted == true && question_id) {
        questions = [await getQuestionById(question_id, orga_id)];
      } else {
        questions = await getAllQuestion(stepId, orga_id);
      }
      setListOfQuestionsByStepId(questions);
    } catch (error) {
      console.error("Error fetching questions:", error);
    } finally {
      setIsLoaded(false);
    }
  }

  useEffect(() => {
    console.log(visibleQuestions);
    console.log("Questions to Update : : ", questionToUpdate);
  }, [visibleQuestions, questionToUpdate]);

  useEffect(async () => {
    if (
      questionToUpdate.length > 0 &&
      visibleQuestions.length < questionToUpdate.length
    ) {
      const res = await saveUpdates();
      if (res === 200) {
        if (stepComleted && question_id) {
          handleGoBack();
        }
      } else {
        setIsLoaded(false);
        if (stepComleted && question_id) {
          handleGoBack();
        }
      }
    }
  }, [visibleQuestions, stepId, questionToUpdate]);

  useEffect(() => {
    if (stepComleted === true && question_id) {
      const filtered = listOfSteps.filter((step) => step.identifier === stepId);
      setFilteredSteps(filtered);
    } else {
      setFilteredSteps(listOfSteps);
    }
  }, [stepComleted, question_id, stepId, listOfSteps]);

  useEffect(() => {
    fetchSteps();
  }, [orga_id, stepId, listOfQuestionsByStepId, isLoaded]);

  useEffect(() => {
    setListOfQuestionsByStepId([]);
    setQuestionToUpdate([]);
    fetchQuestionsByStep();
  }, [stepId]);

  // useEffect(() => {
  //   if (visibleQuestions == []) {
  //       setTimeout(() => {
  //         setQuestionToUpdate([]);
  //       }, 500);
  //   }
  // }, [visibleQuestions]);

  useEffect(() => {
    if (listOfQuestionsByStepId?.length > 0 && visibleQuestions?.length === 0) {
      const firstQuestion = listOfQuestionsByStepId[0];
      addQuestion(firstQuestion);
      setLastAddedIdentifierByNext(firstQuestion.identifier);
    }
  }, [listOfQuestionsByStepId]);

  if (isLoaded) {
    return <DataLoading text={loadingText} />;
  }

  return (
    <>
      {isAuth && (
        <>
          {/* Mobile Sidebar */}
          {isMobile && (
            <Box sx={{ position: "fixed", top: 0, left: 0, zIndex: 1000 }}>
              <IconButton
                onClick={() => setIsSidebarOpen(true)}
                sx={{ ml: 2, mt: 2 }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          )}

          {/* Drawer for Mobile Sidebar */}
          <Drawer
            anchor="left"
            open={isSidebarOpen}
            onClose={() => setIsSidebarOpen(false)}
            PaperProps={{ sx: { width: "40vh", maxWidth: "300px" } }}
          >
            <Box p={2}>
              <Box>
                <IconButton onClick={handleGoBack}>
                  <KeyboardBackspaceIcon />
                </IconButton>
                <span className="return-button">{t("back_to_dashboard")}</span>
              </Box>
              <StepList
                stepId={stepId}
                questionToUpdate={questionToUpdate}
                handleChangeValue={handleChangeValue}
                listOfSteps={filteredSteps}
              />
            </Box>
          </Drawer>

          {/* Desktop View */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              height: "100vh",
            }}
          >
            {/* Menu Section (Hidden on Mobile) */}
            {!isMobile && (
              <Box
                sx={{
                  padding: "10px",
                  width: "40vh",
                }}
              >
                <Box>
                  <IconButton onClick={handleGoBack}>
                    <KeyboardBackspaceIcon />
                  </IconButton>
                  <span className="return-button">
                    {t("back_to_dashboard")}
                  </span>
                </Box>
                <StepList
                  questionToUpdate={questionToUpdate}
                  stepId={stepId}
                  handleChangeValue={handleChangeValue}
                  listOfSteps={filteredSteps}
                />
              </Box>
            )}

            {/* Vertical Line (Hidden on Mobile) */}
            {!isMobile && (
              <Box
                sx={{
                  background: "black",
                  width: "1px",
                  height: "100vh",
                  marginX: 2,
                }}
              />
            )}

            {/* Scrollable Context Section */}
            <Box
              sx={{
                flex: 1,
                margin: "20px",
                padding: "1px",
                overflowY: "auto",
                maxHeight: "95vh",
              }}
            >
              <Box style={{ textAlign: "center" }}>
                <Box style={{ width: "auto", margin: "auto" }}>
                  <Typography variant="subtitle1" sx={{ fontSize: "30px" }}>
                    {t("profile_organization_title")}
                  </Typography>
                  <p style={{ width: "100%" }}>
                    {t("profile_organization_description")}
                  </p>
                </Box>
              </Box>

              {/* Render all visible questions */}
              {visibleQuestions.map((question, index) => (
                <Box
                  key={question.identifier}
                  ref={(el) => (questionRefs.current[index] = el)}
                >
                  <EditQuestions
                    key={question.identifier}
                    addToUpdateQuestions={addToUpdateQuestions}
                    deleteFromQuestionToUpdate={deleteFromQuestionToUpdate}
                    isLoaded={isLoaded}
                    question={question}
                    addQuestion={addQuestion}
                    goToNextQuestion={goToNextQuestion}
                    deleteQuestionsAfterIndex={deleteQuestionsAfterIndex}
                    isVisible={isVisible}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </>
      )}
    </>
  );
}
